<script lang="ts" setup>
import moment from 'moment'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import EmptyState from '@/components/ui/EmptyState.vue'
import avatar from '@images/placeholder/avatar.png'
import { useNotificationStore } from '~/stores/notification'

const { $api } = useNuxtApp()
const notifications = ref({})
const isLoadingNotification = ref(true)
const router = useRouter()
const isShowMenu = ref(false)
const { t } = useI18n()

const { unread_counts } = storeToRefs(useNotificationStore())

const query = ref({
    page: 1,
    limit: 10,
})

const fetchNotification = async (isMenuOpen: boolean) => {
    if (isMenuOpen) {
        isLoadingNotification.value = true

        const { data: notificationData }
            = await $api.notification.getNotifications({ query: query.value })

        notifications.value = notificationData.value.data

        isLoadingNotification.value = false
    }
}

const dateFormat = date => {
    const today = moment()
    const yesterday = moment().subtract(1, 'day')

    if (moment(date).isSame(today, 'day')) {
        return moment(date).format('[Today], hh:mm A')
    }
    else if (moment(date).isSame(yesterday, 'day')) {
        return moment(date).format('[Yesterday], hh:mm A')
    }
    else if (moment(date).isSame(today, 'year')) {
        return moment(date).format('DD MMM, hh:mm A')
    }
    else {
        return moment(date).format('DD MMM YYYY, hh:mm A')
    }
}

const readNotification = async (notification: any) => {
    if (!notification.read_at) {
        await $api.notification.readNotification({
            params: {
                id: notification.id,
      }
        })
    }

    if (notification.url) {
        router.push(notification.url)
    }
    else {
        router.push('/notification')
    }

    isShowMenu.value = false
    await getUnreadNotification()
}

const showAllNotifications = () => {
    isShowMenu.value = false
    router.push('/notification')
}

const getUnreadNotification = async () => {
    const { data: unreadData } = await $api.notification.getUnreadNotification()

    unread_counts.value = unreadData.value.data.unread_counts
}

onMounted(() => {
    nextTick(async () => {
        await getUnreadNotification()
    })
})

const getNotifications = async (query: any) => {
    isLoadingNotification.value = true

    const { data: notificationsData }
        = await $api.notification.getNotifications({
            query
        })

    notifications.value = notificationsData.value.data

    isLoadingNotification.value = false
}

const readAllNotification = async () => {
    await $api.notification.readAllNotification()
    await getNotifications(query.value)
    await getUnreadNotification()
}

watch(
    query,
    (item) => {
        getNotifications(item)
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
    })
    },
    { deep: true },
)
</script>

<template>
  <IconBtn id="notification-btn">
    <VBadge
      :content="unread_counts"
      :model-value="!!unread_counts"
      color="error"
      bordered
      offset-x="1"
      offset-y="1"
      max="99"
    >
      <VIcon icon="ri-notification-2-line" />
    </VBadge>
    <VMenu
      v-model="isShowMenu"
      width="340"
      offset="15px"
      :close-on-content-click="false"
      activator="parent"
      @update:model-value="fetchNotification"
    >
      <template #activator="{ props }" />
      <VCard class="d-flex flex-column">
        <VCardItem class="notification-section">
          <h4 class="text-h5 text-truncate">
            {{ t('notification') }}
          </h4>
          <template #append>
            <!--
              <VChip
              v-show="!!isAllMarkRead"
              size="small"
              class="me-3"
              variant="tonal"
              color="primary"
              >
              {{ totalUnreadNotifications }} new
              </VChip>
            -->

            <IconBtn
              v-show="unread_counts > 0"
              @click="readAllNotification"
            >
              <VIcon
                color="high-emphasis"
                icon="ri-mail-open-line"
              />
              <!-- :icon="!isAllMarkRead ? 'ri-mail-line' : 'ri-mail-open-line' " -->

              <VTooltip
                activator="parent"
                location="start"
              >
                {{ t('mark_all_as_read') }}
                <!-- {{ !isAllMarkRead ? 'Mark all as unread' : 'Mark all as read' }} -->
              </VTooltip>
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <div v-if="isLoadingNotification">
          <VSkeletonLoader
            v-for="index in 5"
            :key="index"
            type="list-item-avatar-two-line"
          />
        </div>

        <!-- 👉 Notifications list -->
        <PerfectScrollbar
          v-else
          :options="{ wheelPropagation: false }"
          style="max-block-size: 27rem"
        >
          <VList class="py-0">
            <template
              v-for="(notification, index) in notifications"
              :key="notification.title"
            >
              <VDivider v-if="index > 0" />

              <VListItem
                link
                lines="one"
                min-height="66px"
                class="list-item-hover-class py-3"
                @click="readNotification(notification)"
              >
                <div class="d-flex align-start gap-3">
                  <NotificationAvatar
                    :notification="notification"
                    :fallback="avatar"
                  />

                  <div>
                    <p class="text-h6 mb-1">
                      {{ notification.title }}
                    </p>
                    <p
                      class="text-body-2 mb-0"
                      style="
                                                letter-spacing: 0.4px !important;
                                                line-height: 18px;
                                            "
                      v-html="notification.message"
                    />
                    <p
                      class="text-sm text-disabled mb-0"
                      style="
                                                letter-spacing: 0.4px !important;
                                                line-height: 18px;
                                            "
                    >
                      {{
                        dateFormat(
                          notification.created_at,
                        )
                      }}
                    </p>
                  </div>

                  <VSpacer />

                  <div class="d-flex flex-column align-end gap-2">
                    <VIcon
                      v-if="!notification.read_at"
                      color="danger"
                      size="10"
                      icon="ri-circle-fill"
                    />
                  </div>
                </div>
              </VListItem>
            </template>

            <EmptyState
              v-if="!notifications.length"
              icon="ri-notification-2-line"
              :title="t('No Notifications Yet')"
            />
          </VList>
        </PerfectScrollbar>

        <VDivider />

        <!-- 👉 Footer -->
        <VCardText
          v-if="!isLoadingNotification && notifications.length"
          class="pa-4"
        >
          <VBtn
            block
            size="small"
            @click="showAllNotifications"
          >
            {{ t('view_all_notification') }}
          </VBtn>
        </VCardText>
      </VCard>
    </VMenu>
  </IconBtn>
</template>

<style lang="scss">
.notification-section {
    padding: 14px !important;
}

.list-item-hover-class {
    .visible-in-hover {
        display: none;
    }

    &:hover {
        .visible-in-hover {
            display: block;
        }
    }
}
</style>
